import React, { useMemo } from 'react';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Search } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { PortalConfigurationType, VendorProfile } from '@trnsact/trnsact-shared-types';
import { LenderProfile } from 'types';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { FormInput } from 'components/form';
import { MUITable } from 'components/shared/MUITable';
import { DocumentRow } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { useDocumentsAccessData } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/hooks/useDocumentsAccessData';
import { getDocumentsAccessTableColumns } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';
import FileUploadModal from 'pages/VendorOpportunity/FileUploadModal';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { useSelector } from 'react-redux';

interface Props {
  vo: VendorOpportunity;
  vp: VendorProfile;
  lender: LenderProfile;
  onSharedDocumentsChange: (documents: Array<{ documentId: string; lenderAccess: boolean }>) => void;
}

export const DocumentsTab = ({ vo, vp, lender, onSharedDocumentsChange }: Props) => {
  const classes = useStyles();

  const documentsPortalConfiguration = useSelector((state: any) =>
    portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.Documents)
  );

  const {
    isLoading,
    tableData,
    searchValue,
    allowedCount,
    totalCount,
    allowedDocuments,
    setSearchValue,
    handleToggle,
  } = useDocumentsAccessData({ vo, vp, lender, onSharedDocumentsChange });

  const columns = useMemo(
    () => getDocumentsAccessTableColumns({ allowedDocuments, onToggle: handleToggle }),
    [allowedDocuments, handleToggle]
  );

  return (
    <Box className={classes.root}>
      <Typography variant="body1" fontWeight={500} sx={{ mb: 2 }}>
        Please confirm the following documents to share with the lender before submitting
      </Typography>

      <Box className={classes.toolbar}>
        <FormInput
          label="Search"
          variant="outlined"
          value={searchValue}
          sx={{ maxWidth: '50%' }}
          onChange={val => setSearchValue(String(val))}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
        <FileUploadModal
          vo={vo}
          vp={vp}
          equipmentUpload={false}
          documentsPortalConfiguration={documentsPortalConfiguration}
          buttonDescription={() => {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <CloudUploadIcon />
                <Typography variant="button">UPLOAD DOCUMENT</Typography>
              </Box>
            );
          }}
        />
      </Box>

      <Box className={classes.tableWrapper}>
        <MUITable<DocumentRow> columns={columns} tableData={tableData} isLoading={isLoading} />
      </Box>

      <Typography variant="body2" align="right" sx={{ mt: 1 }}>
        {allowedCount} of {totalCount} documents granted access
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    padding: '16px 0',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  tableWrapper: {
    maxHeight: '50vh',
    overflowY: 'auto' as const,
  },
}));
